/**
 * A custom tool as a left-to-right flow: what the AI passes in, the steps, and what comes back. A
 * step's column is one past the deepest step it waits for, so steps that start together share a
 * column. Data moves on wires drawn field to field, from an AI input or another step's output field
 * into the argument it fills; a step set to run after another without taking its data gets an arrow.
 */

import { MarkerType, type Edge, type Node } from '@xyflow/react';
import type { CustomToolInput, CustomToolStep } from '@/types/custom-tool.types';
import { dependenciesOf } from '../custom-tool-draft';
import { ADD_NODE, argPort, INPUTS_NODE, inputPort, outPort, RESULT_NODE, resultPort, RUN_FROM_PORT, RUN_TO_PORT, stepNodeId } from './ports';

const WIDTH = { inputs: 240, step: 320, add: 132, result: 220 } as const;

// First-paint estimates in px, close to the cards (headers h-11, step headers h-14, rows h-7, lists
// in py-1); the canvas replaces them with measured heights.
const ESTIMATE = { header: 44, stepHeader: 56, row: 28, listPadding: 8, add: 32 } as const;

export interface StepNodeData extends Record<string, unknown> {
	stepId: string;
}

/** What a drawn line means, so removing it undoes exactly that; `emphasis` is set while a card is active. */
export type WireData = (
	| { kind: 'input' | 'step'; stepId: string; field: string }
	| { kind: 'after'; stepId: string; dep: string }
) & { emphasis?: 'lit' | 'faded' };

interface FlowGraphInput {
	steps: CustomToolStep[];
	inputs: CustomToolInput[];
	canAdd: boolean;
	/** A card's measured height, once the canvas has drawn it. */
	heightOf: (nodeId: string) => number | undefined;
}

const listHeight = (rows: number): number => rows * ESTIMATE.row + ESTIMATE.listPadding;

/** The fields of a step's result that other steps take, in the order they first appear. */
export const usedOutputs = (steps: CustomToolStep[], stepId: string): string[] => [
	...new Set(steps.flatMap((step) => Object.values(step.args).flatMap((arg) => (arg.source === 'step' && arg.step === stepId ? [arg.path] : []))))
];

const COLUMN_GAP = 88;
const ROW_GAP = 24;

/** How many steps deep a step starts: 0 when it waits for nothing. */
const depths = (steps: CustomToolStep[]): Map<string, number> => {
	const byId = new Map(steps.map((step) => [step.id, step]));
	const depth = new Map<string, number>();
	const visit = (step: CustomToolStep, path: Set<string>): number => {
		const known = depth.get(step.id);
		if (known !== undefined) return known;
		// A loop cannot be saved; drawing it flat keeps the canvas usable while it is fixed.
		const deps = dependenciesOf(step).flatMap((id) => {
			const dep = byId.get(id);
			return dep && !path.has(id) ? [dep] : [];
		});
		const value = deps.length ? 1 + Math.max(...deps.map((dep) => visit(dep, new Set([...path, step.id])))) : 0;
		depth.set(step.id, value);
		return value;
	};
	steps.forEach((step) => visit(step, new Set([step.id])));
	return depth;
};

const wires = (steps: CustomToolStep[]): Edge<WireData>[] =>
	steps.flatMap((step) =>
		Object.entries(step.args).flatMap(([field, arg]): Edge<WireData>[] => {
			const target = { target: stepNodeId(step.id), targetHandle: argPort(field), type: 'wire' };
			if (arg.source === 'input') {
				return [
					{ id: `wire:${step.id}:${field}`, source: INPUTS_NODE, sourceHandle: inputPort(arg.input), data: { kind: 'input', stepId: step.id, field }, ...target }
				];
			}
			if (arg.source === 'step') {
				return [
					{
						id: `wire:${step.id}:${field}`,
						source: stepNodeId(arg.step),
						sourceHandle: outPort(arg.path),
						data: { kind: 'step', stepId: step.id, field },
						...target
					}
				];
			}
			return [];
		})
	);

/** An arrow for each step set to run after another, and every step's outcome into the result. */
const runLines = (steps: CustomToolStep[]): Edge[] => [
	...steps.flatMap((step) =>
		step.after.map(
			(dep): Edge<WireData> => ({
				id: `after:${dep}:${step.id}`,
				source: stepNodeId(dep),
				sourceHandle: RUN_FROM_PORT,
				target: stepNodeId(step.id),
				targetHandle: RUN_TO_PORT,
				type: 'wire',
				data: { kind: 'after', stepId: step.id, dep },
				markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16 }
			})
		)
	),
	...steps.map((step) => ({
		id: `run:${step.id}:result`,
		source: stepNodeId(step.id),
		sourceHandle: RUN_FROM_PORT,
		target: RESULT_NODE,
		targetHandle: resultPort(step.id),
		type: 'smoothstep',
		className: 'opacity-40',
		selectable: false,
		deletable: false
	}))
];

type SizedNode = Pick<Node, 'id' | 'type' | 'data'> & { width: number; height: number };

/**
 * Columns left to right, each stacked from the same top line, so a card that opens up only pushes
 * the cards under it down.
 */
const placeColumns = (columns: SizedNode[][]): Node[] => {
	let x = 0;
	return columns.flatMap((column) => {
		const width = Math.max(...column.map((node) => node.width));
		let y = 0;
		const placed = column.map(({ height, ...node }) => {
			const position = { x: x + (width - node.width) / 2, y };
			y += height + ROW_GAP;
			return { ...node, position, selectable: false, deletable: false };
		});
		x += width + COLUMN_GAP;
		return placed;
	});
};

export function flowGraph({ steps, inputs, canAdd, heightOf }: FlowGraphInput): { nodes: Node[]; edges: Edge[] } {
	const sized = (node: Omit<SizedNode, 'height'>, estimate: number): SizedNode => ({ ...node, height: heightOf(node.id) ?? estimate });
	const depth = depths(steps);
	const deepest = steps.length ? Math.max(...depth.values()) : -1;
	const stepColumns: SizedNode[][] = Array.from({ length: deepest + 1 }, (_, level) =>
		steps
			.filter((step) => depth.get(step.id) === level)
			.map((step) =>
				sized(
					{ id: stepNodeId(step.id), type: 'step', data: { stepId: step.id } satisfies StepNodeData, width: WIDTH.step },
					ESTIMATE.stepHeader + listHeight(Math.max(1, Object.keys(step.args).length)) + ESTIMATE.row
				)
			)
	);
	// A new step waits for nothing, so it lands in the first column, where the add card sits.
	const add = sized({ id: ADD_NODE, type: 'add', data: {}, width: WIDTH.add }, ESTIMATE.add);
	if (canAdd) {
		if (stepColumns.length) stepColumns[0].push(add);
		else stepColumns.push([add]);
	}
	const nodes = placeColumns([
		[sized({ id: INPUTS_NODE, type: 'inputs', data: {}, width: WIDTH.inputs }, ESTIMATE.header + listHeight(inputs.length + 1))],
		...stepColumns,
		[sized({ id: RESULT_NODE, type: 'result', data: {}, width: WIDTH.result }, ESTIMATE.header + listHeight(Math.max(1, steps.length)))]
	]);
	return { nodes, edges: [...runLines(steps), ...wires(steps)] };
}
